import type { RouteRecordRaw } from 'vue-router';
import type { MergeAppRoutesContext } from '@/app-router/types';

export function isCatchAllRoute(route: RouteRecordRaw | undefined): boolean {
  return route?.name === 'error404';
}

export function isProtectedRoute(route: RouteRecordRaw | undefined, context: MergeAppRoutesContext): boolean {
  if (!route?.name || typeof route.name !== 'string') {
    return false;
  }

  if (context.protectedRouteNames.has(route.name)) {
    return true;
  }

  return context.protectedRoutePrefixes.some((prefix) => route.name!.toString().startsWith(prefix));
}

/**
 * Merges API-driven module routes with fixed/codegen routes.
 * Fixed auth, error, admin, and non-module routes are preserved.
 * Catch-all (404) is always placed last.
 */
export function mergeAppRoutes(
  moduleRoutes: RouteRecordRaw[],
  fixedRoutes: RouteRecordRaw[],
  context: MergeAppRoutesContext,
): RouteRecordRaw[] {
  const catchAllRoutes = fixedRoutes.filter(isCatchAllRoute);

  const preservedFixedRoutes = fixedRoutes.filter((route) => {
    if (isCatchAllRoute(route)) {
      return false;
    }

    return !context.moduleRouteNames.has(route.name as string);
  });

  const protectedFirst: RouteRecordRaw[] = [];
  const regularFixed: RouteRecordRaw[] = [];

  for (const route of preservedFixedRoutes) {
    if (isProtectedRoute(route, context)) {
      protectedFirst.push(route);
    } else {
      regularFixed.push(route);
    }
  }

  return [...protectedFirst, ...moduleRoutes, ...regularFixed, ...catchAllRoutes];
}

export function partitionRoutesForSync(routes: RouteRecordRaw[]) {
  const catchAll = routes.filter(isCatchAllRoute);
  const rest = routes.filter((route) => !isCatchAllRoute(route));
  return { rest, catchAll };
}
